// Shop-assignment helpers for the console.
//
// A user (or team) scoped to `shops.viewAll` already sees EVERY shop, so per-shop
// assignment is moot — the picker is replaced by an explanatory banner. This mirrors
// solya-app's `collectRolesGrantingViewAll` (UserShopAccessPanel.helpers), ported to
// the console's RoleDetailDto shape.

import type { RoleDetailDto } from "../api/types.js"

/** The permission key that grants access to ALL of an org's shops. */
export const SHOPS_VIEW_ALL = "shops.viewAll"

/**
 * Every role (of the ones passed) whose permissions include {@link SHOPS_VIEW_ALL}.
 * Order is preserved so a caller can name the FIRST granting role deterministically
 * in the banner.
 */
export function collectRolesGrantingViewAll(roles: RoleDetailDto[]): RoleDetailDto[] {
  return roles.filter((role) => role.permissions.some((p) => p.key === SHOPS_VIEW_ALL))
}

/** How a role is named in the UI: its display name, falling back to the key. */
export function roleLabel(role: { displayName: string; name: string }): string {
  return role.displayName || role.name
}

/**
 * Format-validate shop ids the way the backend does (schema `varchar(36)`): each id
 * must be non-empty and ≤ 36 chars. Opaque strings, not uuids — ported from
 * solya-app's `validateShopIds`. Authoritative membership is enforced server-side;
 * the picker only offers valid shops.
 */
export function validateShopIds(shopIds: readonly string[]): boolean {
  return shopIds.every((id) => id.length > 0 && id.length <= 36)
}
